import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that manages gift wrap variant selection and add-to-cart.
 * Expand/collapse is handled by the parent <accordion-custom> element.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} variantButton - The variant selection buttons.
 * @property {HTMLButtonElement} addButton - The add-to-cart button.
 * @property {HTMLElement} errorMessage - The error message container.
 *
 * @extends {Component<Refs>}
 */
class CartGiftWrapComponent extends Component {
  /** @type {string | null} */
  #selectedVariantId = null;

  connectedCallback() {
    super.connectedCallback();
    const autoVariantId = this.dataset.autoVariantId;
    if (autoVariantId) {
      this.#selectedVariantId = autoVariantId;
    }

  }

  /**
   * Handles variant selection.
   * @param {Event} event - The click event from the variant button.
   */
  selectVariant(event) {
    const button = /** @type {HTMLButtonElement} */ (event.target.closest('.cart-gift-wrap__variant-button'));
    const variantId = button?.dataset.variantId;

    if (!variantId) return;

    this.#selectedVariantId = variantId;

    const { variantButton, addButton } = this.refs;

    if (variantButton) {
      const buttons = Array.isArray(variantButton) ? variantButton : [variantButton];
      for (const btn of buttons) {
        btn.setAttribute('aria-checked', String(btn.dataset.variantId === variantId));
      }
    }

    addButton.disabled = false;
  }

  /**
   * Adds the selected gift wrap variant to the cart.
   */
  async addGiftWrap() {
    if (!this.#selectedVariantId) return;

    const { addButton, errorMessage } = this.refs;
    const addButtonText = addButton.textContent;
    const performanceMarker = cartPerformance.createStartingMarker('gift-wrap:add');

    this.classList.add('cart-gift-wrap--loading');
    addButton.disabled = true;
    addButton.textContent = Theme.translations.adding || 'Adding...';
    errorMessage.setAttribute('hidden', '');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = /** @type {string[]} */ ([]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    const formData = new FormData();
    formData.append('id', this.#selectedVariantId);
    formData.append('quantity', '1');

    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','));
    }

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });

      const data = await response.json();

      if (data.status) {
        errorMessage.removeAttribute('hidden');
        addButton.disabled = false;
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.#selectedVariantId, {
          source: 'cart-gift-wrap-component',
          itemCount: 1,
          variantId: this.#selectedVariantId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error('Gift wrap add to cart failed:', error);
      errorMessage.removeAttribute('hidden');
      addButton.disabled = false;
    } finally {
      this.classList.remove('cart-gift-wrap--loading');
      addButton.textContent = addButtonText;
      cartPerformance.measureFromMarker(performanceMarker);
    }
  }
}

if (!customElements.get('cart-gift-wrap-component')) {
  customElements.define('cart-gift-wrap-component', CartGiftWrapComponent);
}
